import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  finalize,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';
import * as HomeActions from './home.actions';
import {
  deleteConvertedFile,
  deleteConvertedFileFailure,
  deleteConvertedFileSuccess,
  loadConvertedFiles,
  loadConvertedFilesSuccess,
} from './home.actions';
import { Store } from '@ngrx/store';
import { HomeState } from './home.reducer';
import { FilesService } from '../data/services/files.service';
import { EMPTY, forkJoin, of, switchMap, tap } from 'rxjs';
import { selectConvertedFiles } from './home.selectors';
import { NgxSpinnerService } from 'ngx-spinner';

@Injectable()
export class HomeEffects {
  conversionComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.conversionComplete),
      map((action) => action.fileId),
      map((fileId) => HomeActions.deleteUploadedFile({ fileId })),
    ),
  );
  gerberConversionComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.gerberConversionComplete),
      map((action) => action.fileId),
      map((fileId) => HomeActions.deleteGerberFile({ fileId })),
    ),
  );
  lectraConversionComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.lectraConversionComplete),
      map((action) => action.fileId),
      map((fileId) => HomeActions.deleteLectraFile({ fileId })),
    ),
  );
  protected filesService = inject(FilesService);
  deleteConvertedFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteConvertedFile),
      mergeMap(({ fileId }) =>
        this.filesService.deleteConvertedFile(fileId).pipe(
          map(() => deleteConvertedFileSuccess({ fileId })),
          catchError((error) => of(deleteConvertedFileFailure({ error }))),
        ),
      ),
    ),
  );
  loadConvertedFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadConvertedFiles),
      switchMap(() =>
        this.filesService.getAvailableConvertedFiles().pipe(
          map((files) => loadConvertedFilesSuccess({ files })),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );
  loadMonthlyConvertedFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.loadMonthlyConvertedFiles),
      mergeMap(() =>
        this.filesService.getMonthlyConvertedFiles().pipe(
          map((files) =>
            HomeActions.loadMonthlyConvertedFilesSuccess({ files }),
          ),
          catchError((error) => {
            return of(
              HomeActions.loadMonthlyConvertedFilesError({
                error: error.message || 'Failed to load monthly files',
              }),
            );
          }),
        ),
      ),
    ),
  );
  protected spinner = inject(NgxSpinnerService);
  requestClearConvertedFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.requestClearConvertedFiles),
      tap(() => this.spinner.show('main')),
      withLatestFrom(this.store.select(selectConvertedFiles)),
      mergeMap(([, files]) => {
        if (!files || files.length === 0) {
          return of(HomeActions.clearConvertedFiles());
        }

        const deleteRequests = files.map((file) =>
          this.filesService.deleteConvertedFile(file.fileId),
        );

        return forkJoin(deleteRequests).pipe(
          map(() => HomeActions.clearConvertedFiles()),
          catchError((error) => {
            console.error('Error while deleting files: ', error);
            return of(HomeActions.clearConvertedFiles());
          }),
        );
      }),
      tap(() => this.spinner.hide('main')),
      finalize(() => this.spinner.hide('main')),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<HomeState>,
  ) {}
}
